import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTelegramWebApp } from '../../../hooks/useTelegramWebApp'
import './KinescopePlayer.css'

interface KinescopePlayerProps {
  src: string
  height: number
  caption?: string
  blockId: string
}

const KinescopePlayer: React.FC<KinescopePlayerProps> = ({
  src,
  height,
  caption,
  blockId,
}) => {
  const { platform, webApp } = useTelegramWebApp()
  // Показываем кнопку полноэкранного режима только для Android
  const showFullscreenButton = platform === 'android'
  const [isFullscreen, setIsFullscreen] = useState(false)

  const playerRef = useRef<HTMLDivElement>(null)

  // Создаем функцию для блокировки touch событий, чтобы можно было правильно их удалить
  const preventScrollRef = useRef<((e: TouchEvent) => void) | null>(null)

  // Сохраняем позицию скролла для восстановления
  const scrollPositionRef = useRef<number>(0)

  // Для сохранения состояния видео между обычным и полноэкранным режимом
  const [currentVideoSrc, setCurrentVideoSrc] = useState(src)

  // Ref'ы для iframe'ов чтобы получать доступ к видео
  const normalIframeRef = useRef<HTMLIFrameElement>(null)
  const fullscreenIframeRef = useRef<HTMLIFrameElement>(null)

  // Сохраняем текущее время воспроизведения
  const videoTimeRef = useRef<number>(0)
  const isVideoPlayingRef = useRef<boolean>(false)

  // Функция для добавления автоплея и времени начала к URL
  const addVideoStateToUrl = (
    baseUrl: string,
    currentTime?: number,
    autoplay?: boolean
  ) => {
    try {
      const url = new URL(baseUrl)

      if (currentTime && currentTime > 0) {
        url.searchParams.set('t', Math.floor(currentTime).toString())
      }

      if (autoplay !== undefined) {
        url.searchParams.set('autoplay', autoplay ? '1' : '0')
      }

      return url.toString()
    } catch (error) {
      console.warn('Ошибка при обработке URL видео:', error)
      return baseUrl
    }
  }

  // Функция для попытки получения текущего времени через postMessage
  const tryGetVideoTime = (iframe: HTMLIFrameElement | null) => {
    if (!iframe?.contentWindow) return

    try {
      // Отправляем запрос на получение текущего времени
      iframe.contentWindow.postMessage({ action: 'getCurrentTime' }, '*')
    } catch (error) {
      console.warn('Не удалось получить время видео:', error)
    }
  }

  const handleFullscreen = () => {
    if (!isFullscreen) {
      // Попытаемся получить текущее время видео перед переключением
      tryGetVideoTime(normalIframeRef.current)

      // Даем небольшую задержку для получения ответа от плеера
      setTimeout(() => {
        // Сохраняем текущую позицию скролла
        scrollPositionRef.current =
          window.pageYOffset || document.documentElement.scrollTop

        // Обновляем URL видео с текущим временем и автоплеем
        const newVideoSrc = addVideoStateToUrl(src, videoTimeRef.current, true)
        setCurrentVideoSrc(newVideoSrc)

        // Переходим в полноэкранный режим
        setIsFullscreen(true)

        // Блокируем скролл страницы и touch события
        document.body.style.overflow = 'hidden'
        document.body.style.position = 'fixed'
        document.body.style.top = `-${scrollPositionRef.current}px`
        document.body.style.width = '100%'
        document.body.style.height = '100%'

        // Дополнительные меры для мобильных устройств
        document.documentElement.style.overflow = 'hidden'

        // Блокируем touch события на документе (только scroll, не все touch события)
        preventScrollRef.current = (e: TouchEvent) => {
          // Проверяем, что это не клик по кнопке закрытия
          const target = e.target as Element
          if (
            target?.closest('.fullscreen-btn') ||
            target?.closest('.kinescope-player.fullscreen')
          ) {
            return // Разрешаем touch события на видео и кнопке
          }
          e.preventDefault()
          e.stopPropagation()
        }
        document.addEventListener('touchmove', preventScrollRef.current, {
          passive: false,
        })
      }, 100)
    } else {
      // Попытаемся получить текущее время видео перед закрытием
      tryGetVideoTime(fullscreenIframeRef.current)

      // Даем небольшую задержку для получения ответа от плеера
      setTimeout(() => {
        // Обновляем URL видео с текущим временем (без автоплея для обычного режима)
        const newVideoSrc = addVideoStateToUrl(src, videoTimeRef.current, false)
        setCurrentVideoSrc(newVideoSrc)

        // Выходим из полноэкранного режима
        setIsFullscreen(false)
      }, 100)

      // Восстанавливаем скролл
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
      document.body.style.height = ''
      document.documentElement.style.overflow = ''

      // Восстанавливаем позицию скролла с небольшой задержкой для плавности
      setTimeout(() => {
        window.scrollTo(0, scrollPositionRef.current)
      }, 0)

      // Разблокируем touch события
      if (preventScrollRef.current) {
        document.removeEventListener('touchmove', preventScrollRef.current)
        preventScrollRef.current = null
      }
    }
  }

  const handleEscapeKey = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && isFullscreen) {
      handleFullscreen()
    }
  }

  // Обработка клавиши Escape через document
  useEffect(() => {
    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        handleFullscreen()
      }
    }

    if (isFullscreen) {
      document.addEventListener('keydown', handleDocumentKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown)
    }
  }, [isFullscreen])

  // Обработка кнопки "Назад" Telegram для закрытия полноэкранного режима
  useEffect(() => {
    if (!webApp?.BackButton || !isFullscreen) return

    const handleBackButton = () => {
      if (isFullscreen) {
        handleFullscreen() // Закрываем полноэкранный режим
      }
    }

    // Добавляем обработчик на кнопку "Назад"
    webApp.BackButton.onClick(handleBackButton)

    return () => {
      // Удаляем обработчик при размонтировании или изменении состояния
      if (webApp.BackButton.offClick) {
        webApp.BackButton.offClick(handleBackButton)
      }
    }
  }, [isFullscreen, webApp])

  // Слушаем сообщения от Kinescope плеера для получения времени воспроизведения
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Проверяем, что сообщение от Kinescope
      if (
        event.origin.includes('kinescope.io') ||
        event.origin.includes('kinescope.ru')
      ) {
        try {
          const data =
            typeof event.data === 'string' ? JSON.parse(event.data) : event.data

          if (
            data.type === 'currentTime' &&
            typeof data.currentTime === 'number'
          ) {
            videoTimeRef.current = data.currentTime
          }

          if (
            data.type === 'timeupdate' &&
            typeof data.currentTime === 'number'
          ) {
            videoTimeRef.current = data.currentTime
          }

          if (data.type === 'play') {
            isVideoPlayingRef.current = true
          }

          if (data.type === 'pause') {
            isVideoPlayingRef.current = false
          }
        } catch (error) {
          // Игнорируем ошибки парсинга
        }
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [])

  // Очистка при размонтировании компонента
  useEffect(() => {
    return () => {
      if (isFullscreen) {
        document.body.style.overflow = ''
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.width = ''
        document.body.style.height = ''
        document.documentElement.style.overflow = ''

        // Восстанавливаем позицию скролла
        setTimeout(() => {
          window.scrollTo(0, scrollPositionRef.current)
        }, 0)

        if (preventScrollRef.current) {
          document.removeEventListener('touchmove', preventScrollRef.current)
          preventScrollRef.current = null
        }
      }
    }
  }, [isFullscreen])

  // Рендер полноэкранного режима через портал
  const renderFullscreenPlayer = () => {
    if (!isFullscreen) return null

    return createPortal(
      <>
        {/* Overlay для блокировки взаимодействия со страницей */}
        <div
          className="fullscreen-overlay"
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
            handleFullscreen()
          }}
        />

        {/* Полноэкранный плеер */}
        <div
          ref={playerRef}
          className="kinescope-player fullscreen"
          onKeyDown={handleEscapeKey}
          tabIndex={-1}
        >
          <div className="player-container">
            <iframe
              ref={fullscreenIframeRef}
              src={currentVideoSrc}
              width="100%"
              height="100vh"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
              allowFullScreen
              sandbox="allow-scripts allow-same-origin"
              title={`Видео ${blockId}`}
            />

            {/* Кнопка закрытия полноэкранного режима */}
            <button
              className="fullscreen-btn"
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                handleFullscreen()
              }}
              aria-label="Выйти из полноэкранного режима"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        </div>
      </>,
      document.body
    )
  }

  // Показываем кастомный плеер с кнопкой полноэкранного режима (для тестирования - на всех платформах)
  if (showFullscreenButton) {
    return (
      <>
        {/* Обычный плеер */}
        <div className="kinescope-player">
          <div className="player-container">
            <iframe
              ref={normalIframeRef}
              src={currentVideoSrc}
              width="100%"
              height={height}
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
              allowFullScreen
              sandbox="allow-scripts allow-same-origin"
              title={`Видео ${blockId}`}
            />

            {/* Кнопка полноэкранного режима */}
            <button
              className="fullscreen-btn"
              onClick={handleFullscreen}
              aria-label="Полноэкранный режим"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>

          {caption && <p className="embed-caption">{caption}</p>}
        </div>

        {/* Полноэкранный плеер через портал */}
        {renderFullscreenPlayer()}
      </>
    )
  }

  // Для других платформ показываем обычный iframe
  return (
    <div className="kinescope-player">
      <div className="embed-wrapper">
        <iframe
          src={currentVideoSrc}
          width="100%"
          height={height}
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
          allowFullScreen
          sandbox="allow-scripts allow-same-origin"
          title={`Видео ${blockId}`}
        />
      </div>
      {caption && <p className="embed-caption">{caption}</p>}
    </div>
  )
}

export default KinescopePlayer
